import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { TalentType } from "./types";
import { useTalent } from "./TalentContext";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MultiSelect } from "@/components/ui/multi-select";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { RefreshCw, Save, X } from "lucide-react";

// Define the form schema with zod
export const talentFormSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  phone: z.string().optional(),
  position: z.string(),
  level: z.string(),
  skills: z.array(z.string()),
  location: z.string().optional(),
  availability: z.string().optional(),
  salary: z.string().optional(),
  resumeUrl: z.string().url().optional().or(z.literal("")),
  portfolioUrl: z.string().url().optional().or(z.literal("")),
  linkedInUrl: z.string().url().optional().or(z.literal("")),
  githubUrl: z.string().url().optional().or(z.literal("")),
  notes: z.string().optional(),
  status: z.string(),
});

export const levelOptions = [
  "Junior",
  "Middle",
  "Senior",
  "Lead",
  "Principal",
];

export const statusOptions = [
  "Available",
  "In process",
  "Hired",
];

// Form value type
export type TalentFormValues = z.infer<typeof talentFormSchema>;

type TalentProfileEditFormProps = {
  talent: TalentType;
  onCancel: () => void;
  onSuccess: () => void;
};

export function TalentProfileEditForm({ talent, onCancel, onSuccess }: TalentProfileEditFormProps) {
  const { updateTalent } = useTalent();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formChanged, setFormChanged] = useState(false);
  
  // Default skill options
  const defaultSkillOptions = [
    "JavaScript", 
    "TypeScript", 
    "React", 
    "Node.js", 
    "Python",
    "Java",
    "C#",
    "PHP",
    "Go",
    "Ruby",
    "Swift"
  ];
  
  // Initialize skill options from the talent's skills
  const [skillOptions, setSkillOptions] = useState<string[]>([
    ...talent.skills,
    ...defaultSkillOptions.filter(skill => !talent.skills.includes(skill))
  ]);

  // Create form
  const form = useForm<TalentFormValues>({
    resolver: zodResolver(talentFormSchema),
    defaultValues: {
      name: talent.name,
      email: talent.email,
      phone: talent.phone || "",
      position: talent.position,
      level: talent.level,
      skills: talent.skills,
      location: talent.location || "",
      availability: talent.availability || "",
      salary: talent.salary || "",
      resumeUrl: talent.resumeUrl || "",
      portfolioUrl: talent.portfolioUrl || "",
      linkedInUrl: talent.linkedInUrl || "",
      githubUrl: talent.githubUrl || "",
      notes: talent.notes || "",
      status: talent.status,
    },
  });

  // Update form values when talent changes
  useEffect(() => {
    form.reset({
      name: talent.name,
      email: talent.email,
      phone: talent.phone || "",
      position: talent.position,
      level: talent.level,
      skills: talent.skills,
      location: talent.location || "",
      availability: talent.availability || "",
      salary: talent.salary || "",
      resumeUrl: talent.resumeUrl || "",
      portfolioUrl: talent.portfolioUrl || "",
      linkedInUrl: talent.linkedInUrl || "",
      githubUrl: talent.githubUrl || "",
      notes: talent.notes || "",
      status: talent.status,
    });
    setFormChanged(false);
  }, [talent, form]);
  
  // Subscribe to form changes to enable/disable reset button
  useEffect(() => {
    const subscription = form.watch(() => {
      setFormChanged(true);
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // Handle form submission
  const onSubmit = async (values: TalentFormValues) => {
    setIsSubmitting(true);
    try {
      await updateTalent(talent._id, {
        ...values,
        // Ensure we're not sending empty strings for optional fields
        location: values.location || undefined,
        availability: values.availability || undefined,
        salary: values.salary || undefined,
        resumeUrl: values.resumeUrl || undefined,
        portfolioUrl: values.portfolioUrl || undefined,
        linkedInUrl: values.linkedInUrl || undefined,
        githubUrl: values.githubUrl || undefined,
        notes: values.notes || undefined,
      });
      
      setFormChanged(false);
      onSuccess();
    } catch (error) {
      console.error("Failed to update talent:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle adding custom skills
  const handleAddSkill = (skill: string) => {
    if (!skillOptions.includes(skill)) {
      setSkillOptions(prev => [...prev, skill]);
    }
  };
  
  // Reset form to original values
  const handleReset = () => {
    form.reset({
      name: talent.name,
      email: talent.email,
      phone: talent.phone || "",
      position: talent.position,
      level: talent.level,
      skills: talent.skills,
      location: talent.location || "",
      availability: talent.availability || "",
      salary: talent.salary || "",
      resumeUrl: talent.resumeUrl || "",
      portfolioUrl: talent.portfolioUrl || "",
      linkedInUrl: talent.linkedInUrl || "",
      githubUrl: talent.githubUrl || "",
      notes: talent.notes || "",
      status: talent.status,
    });
    setFormChanged(false);
  };
  
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Basic Information */}
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="Full name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder="Email address" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone</FormLabel>
                <FormControl>
                  <Input placeholder="Phone number" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="location"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Location</FormLabel>
                <FormControl>
                  <Input placeholder="City, Country" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Professional Information */}
          <FormField
            control={form.control}
            name="position"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Position</FormLabel>
                <FormControl>
                  <Input placeholder="Job title" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="level"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Level</FormLabel>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select level" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {levelOptions.map(level => (
                      <SelectItem key={level} value={level}>
                        {level}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {statusOptions.map(status => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="skills"
            render={({ field }) => (
              <FormItem className="col-span-1 md:col-span-2">
                <FormLabel>Skills</FormLabel>
                <FormControl>
                  <MultiSelect
                    placeholder="Select skills"
                    selected={field.value}
                    options={skillOptions.map(skill => ({
                      label: skill,
                      value: skill,
                    }))}
                    onChange={field.onChange}
                    onCreateOption={handleAddSkill}
                    creatable
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Additional Information */}
          <FormField
            control={form.control}
            name="availability"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Availability</FormLabel>
                <FormControl>
                  <Input placeholder="Immediate, 2 weeks, etc." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="salary"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Salary Expectation</FormLabel>
                <FormControl>
                  <Input placeholder="Salary range" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Links */}
          <FormField
            control={form.control}
            name="resumeUrl"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Resume URL</FormLabel>
                <FormControl>
                  <Input placeholder="https://..." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="portfolioUrl"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Portfolio URL</FormLabel>
                <FormControl>
                  <Input placeholder="https://..." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="linkedInUrl"
            render={({ field }) => (
              <FormItem>
                <FormLabel>LinkedIn URL</FormLabel>
                <FormControl>
                  <Input placeholder="https://linkedin.com/in/..." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="githubUrl"
            render={({ field }) => (
              <FormItem>
                <FormLabel>GitHub URL</FormLabel>
                <FormControl>
                  <Input placeholder="https://github.com/..." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Notes */}
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem className="col-span-1 md:col-span-2">
                <FormLabel>Notes</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Additional notes about the talent..."
                    className="min-h-[100px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            disabled={!formChanged || isSubmitting}
            className="gap-1"
          >
            <RefreshCw className="h-4 w-4" />
            Reset
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
            className="gap-1"
          >
            <X className="h-4 w-4" />
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting} className="gap-1">
            {isSubmitting ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
} 